import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp, FaPlus, FaTrash } from "react-icons/fa";
import { crm, Pill } from "../../theme";
import { NcmTaxRate } from "../../models/Tax";
import { getNcmTaxRate } from "../../services/taxService";
import { CommercialClientBranch } from "../../models/CommercialClient";
import { BillingSetting } from "../../models/BillingSetting";
import { resolveRates } from "./fiscal";
import { TableCard, tdProps, thProps } from "../process/components/tableStyles";
import { toneColors } from "../process/components/duimp";
import { DEFAULT_COFINS_RATE, DEFAULT_PIS_RATE } from "./contaOrdem";
import { calculateEncomenda, defaultEncomendaParams, EncomendaParams } from "./encomenda";
import { formatMoney } from "./EstimateSheet";

interface ItemDraft {
  key: number;
  ncm: string;
  description: string;
  quantity: string;
  unitValue: string;
  unitWeight: string;
  ii: string;
  ipi: string;
  pis: string;
  cofins: string;
  dumping: string;
  reduction: string;
  /** ICMS destacado do item (%): vazio = parâmetro "ICMS destacado". */
  icms: string;
  mva: string;
  internal: string;
  roadFreight: string;
  /** Da parametrização (não editáveis na tabela): DIFAL e IBS/CBS. */
  difalInternal?: number | null;
  difalInterstate?: number | null;
  difalFcp?: number | null;
  ibs?: number | null;
  cbs?: number | null;
  /** Exceção da parametrização aplicada ao item (rótulo). */
  fiscalNote?: string;
  rates?: NcmTaxRate | null;
}

const parseNumber = (value: string): number | null => {
  const text = value.trim();
  if (!text) return null;
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};
const num = (value: string) => parseNumber(value) ?? 0;
const toDraft = (value?: number | null) => (value == null ? "" : String(value).replace(".", ","));
const fmt = (value: number, digits = 2) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

let nextKey = 1;
const newItem = (): ItemDraft => ({
  key: nextKey++,
  ncm: "",
  description: "",
  quantity: "1",
  unitValue: "",
  unitWeight: "",
  ii: "",
  ipi: "",
  pis: toDraft(DEFAULT_PIS_RATE),
  cofins: toDraft(DEFAULT_COFINS_RATE),
  dumping: "",
  reduction: "",
  icms: "",
  mva: "",
  internal: "",
  roadFreight: "",
});

type ParamDrafts = Record<keyof EncomendaParams, string>;
const initialParams = Object.fromEntries(
  Object.entries(defaultEncomendaParams).map(([k, v]) => [k, toDraft(v)]),
) as ParamDrafts;

const paramLabels: [keyof EncomendaParams, string][] = [
  ["marginRate", "Margem de contribuição líquida (%)"],
  ["icmsRate", "ICMS destacado na NF (%)"],
  ["ttdRate", "ICMS efetivo — TTD 410 (%)"],
  ["pisRate", "PIS na venda (%)"],
  ["cofinsRate", "COFINS na venda (%)"],
  ["incomeTaxRate", "IRPJ/CSLL no preço (%)"],
  ["icmsDiscountRate", "Desconto de ICMS (% do ICMS)"],
  ["irpjRate", "IRPJ s/ margem bruta (%)"],
  ["csllRate", "CSLL s/ margem bruta (%)"],
  ["monthlyInterestRate", "Juros ao mês (%)"],
  ["downPaymentShare", "Antecipado (% do CIF)"],
  ["downPaymentMonths", "Prazo antecipado (meses)"],
  ["balanceMonths", "Prazo saldo (meses)"],
  ["nationalizationMonths", "Prazo nacionalização (meses)"],
];

const Card = ({ title, subtitle, children, action }: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }) => (
  <Box w="95%" bg="white" borderRadius={crm.radius} border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} p="24px" mb="20px">
    <Flex justify="space-between" align="flex-start" gap="12px">
      <Box>
        <Text fontSize="16px" fontWeight="700" color={crm.text}>{title}</Text>
        {subtitle && <Text fontSize="13px" color={crm.textMuted} mt="2px">{subtitle}</Text>}
      </Box>
      {action}
    </Flex>
    <Box mt="16px">{children}</Box>
  </Box>
);

const Field = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <Box>
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{label}</Text>
    {children}
    {hint && <Text fontSize="11px" color={crm.textFaint} mt="2px">{hint}</Text>}
  </Box>
);

const Stat = ({ label, value, highlight, hint }: { label: string; value: string; highlight?: boolean; hint?: string }) => (
  <Box p="14px 16px" borderRadius={crm.radiusSm} border={`1px solid ${highlight ? "#c7d7fe" : crm.border}`} bg={highlight ? crm.brandSoft : crm.subtle}>
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>{label}</Text>
    <Text fontSize="20px" fontWeight="700" color={highlight ? crm.brandDark : crm.text} mt="4px">{value}</Text>
    {hint && <Text fontSize="11px" color={crm.textFaint}>{hint}</Text>}
  </Box>
);

/** Campos do item vindos da parametrização (exceção por NCM / UF da filial / cliente, como no Gett). */
const fiscalPatch = (setting: BillingSetting | null, ncm: string, state?: string | null, customerName?: string): Partial<ItemDraft> => {
  if (!setting) return {};
  const { rates, exception } = resolveRates(setting, { ncm, state, customerName });
  const st = (rates.mvaPercent ?? 0) > 0 && (rates.stInternalPercent ?? 0) > 0;
  return {
    icms: toDraft(rates.icmsPercent),
    mva: st ? toDraft(rates.mvaPercent) : "",
    internal: st ? toDraft(rates.stInternalPercent) : "",
    reduction: st && (rates.stBaseReductionPercent ?? 0) > 0 ? toDraft(rates.stBaseReductionPercent) : "",
    difalInternal: rates.difalInternalPercent,
    difalInterstate: rates.difalInterstatePercent,
    difalFcp: rates.difalFcpPercent,
    ibs: (rates.ibsStatePercent ?? 0) + (rates.ibsCityPercent ?? 0),
    cbs: rates.cbsPercent,
    fiscalNote: exception
      ? [exception.ncm && `NCM ${exception.ncm}`, exception.state && `UF ${exception.state}`, exception.customerName].filter(Boolean).join(" · ")
      : undefined,
  };
};

/**
 * Simulador por ENCOMENDA (planilha "Calculo NF ALFA ENCOMENDA ... REDUÇÃO ICMS ST"): custo de importação,
 * preço de venda com margem e impostos e a NF de saída com ST. ICMS, ST/MVA, redução da BC ST, DIFAL e IBS/CBS
 * vêm da parametrização fiscal (Gett) pelo NCM do item e a UF da filial; dá para editar ICMS/ST na tabela.
 */
const EncomendaSimulator = ({
  dollarRate,
  branch,
  setting,
  customerName,
}: {
  dollarRate?: number;
  branch: CommercialClientBranch | null;
  setting: BillingSetting | null;
  customerName?: string;
}) => {
  const [exchange, setExchange] = useState("");
  const [freight, setFreight] = useState("");
  const [insurance, setInsurance] = useState("");
  const [prepaid, setPrepaid] = useState("");
  const [payments, setPayments] = useState([{ amount: "", rate: "" }, { amount: "", rate: "" }]);
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);

  const [afrmm, setAfrmm] = useState("");
  const [storage, setStorage] = useState("");
  const [broker, setBroker] = useState("");
  const [destinationFees, setDestinationFees] = useState("");
  const [license, setLicense] = useState("");
  const [ibama, setIbama] = useState("");
  const [daytona, setDaytona] = useState("");
  const [crossdocking, setCrossdocking] = useState("");
  const [roadFreightExpense, setRoadFreightExpense] = useState("");
  const [helpers, setHelpers] = useState("");
  const [siscomex, setSiscomex] = useState("");

  const [paramDrafts, setParamDrafts] = useState<ParamDrafts>(initialParams);
  const [showParams, setShowParams] = useState(false);

  useEffect(() => {
    if (dollarRate && !exchange) setExchange(dollarRate.toFixed(4).replace(".", ","));
  }, [dollarRate]);

  // Parametrização/filial mudou: reaplica em todos os itens; PIS/COFINS da venda vêm da parametrização
  const previousSettingId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (setting) {
      setItems((list) => list.map((i) => ({ ...i, ...fiscalPatch(setting, i.ncm, branch?.state, customerName) })));
      setParamDrafts((p) => ({
        ...p,
        ...(setting.rates.icmsPercent != null && { icmsRate: toDraft(setting.rates.icmsPercent) }),
        ...(setting.rates.pisPercent != null && { pisRate: toDraft(setting.rates.pisPercent) }),
        ...(setting.rates.cofinsPercent != null && { cofinsRate: toDraft(setting.rates.cofinsPercent) }),
      }));
    } else if (previousSettingId.current) {
      setItems((list) =>
        list.map((i) => ({
          ...i, icms: "", difalInternal: null, difalInterstate: null, difalFcp: null, ibs: null, cbs: null, fiscalNote: undefined,
        })),
      );
      setParamDrafts(initialParams);
    }
    previousSettingId.current = setting?.id;
  }, [setting?.id, branch?.id, customerName]);

  const params = useMemo(
    () => Object.fromEntries(Object.entries(paramDrafts).map(([k, v]) => [k, parseNumber(v) ?? 0])) as unknown as EncomendaParams,
    [paramDrafts],
  );

  const totalKg = items.reduce((sum, i) => sum + num(i.quantity) * num(i.unitWeight), 0);
  // Planilha: IBAMA = toneladas × 0,7 × 80
  const ibamaAuto = (totalKg / 1000) * 0.7 * 80;
  const expensesBrl =
    num(afrmm) + num(storage) + num(broker) + num(destinationFees) + num(license) +
    (parseNumber(ibama) ?? ibamaAuto) + num(daytona) + num(crossdocking) + num(roadFreightExpense) + num(helpers) + num(siscomex);

  const fx = num(exchange);
  const result = calculateEncomenda({
    exchangeRate: fx,
    freight: num(freight),
    insuranceBrl: num(insurance),
    prepaidFees: num(prepaid),
    expensesBrl,
    payments: payments.map((p) => ({ amount: num(p.amount), rate: num(p.rate) })).filter((p) => p.amount > 0 && p.rate > 0),
    params,
    items: items.map((i) => ({
      quantity: num(i.quantity),
      unitValue: num(i.unitValue),
      unitWeight: num(i.unitWeight),
      iiRate: num(i.ii),
      ipiRate: num(i.ipi),
      pisRate: num(i.pis),
      cofinsRate: num(i.cofins),
      dumping: num(i.dumping),
      icmsReductionRate: parseNumber(i.reduction),
      icmsRate: parseNumber(i.icms),
      mvaRate: parseNumber(i.mva),
      internalIcmsRate: num(i.internal),
      roadFreightBrl: num(i.roadFreight),
      difalInternalRate: i.difalInternal,
      difalInterstateRate: i.difalInterstate,
      difalFcpRate: i.difalFcp,
      ibsRate: i.ibs,
      cbsRate: i.cbs,
    })),
  });
  const totalFob = result.totals.fob;
  // MVA informada sem alíquota interna: a ST desse item não sai (ver encomenda.ts)
  const missingSt = items.some((i) => parseNumber(i.mva) != null && (parseNumber(i.internal) ?? 0) <= 0);
  const ready = fx > 0 && totalFob > 0;

  const updateItem = (key: number, patch: Partial<ItemDraft>) =>
    setItems((list) => list.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const lookupNcm = async (item: ItemDraft) => {
    const digits = item.ncm.replace(/\D/g, "");
    if (digits.length !== 8) return updateItem(item.key, { rates: undefined });
    if (item.rates?.ncm === digits) return;
    const fiscal = fiscalPatch(setting, digits, branch?.state, customerName);
    try {
      const rates = await getNcmTaxRate(digits);
      updateItem(item.key, {
        ...fiscal,
        rates: rates ?? null,
        description: item.description || rates?.description?.slice(0, 120) || "",
        ...(rates && {
          ii: toDraft(rates.iiRate ?? 0),
          ipi: toDraft(rates.ipiNotTaxed ? 0 : rates.ipiRate ?? 0),
          pis: toDraft(rates.pisRate ?? rates.defaultPisRate),
          cofins: toDraft(rates.cofinsRate ?? rates.defaultCofinsRate),
        }),
      });
    } catch {
      updateItem(item.key, { ...fiscal, rates: undefined });
    }
  };

  const input = (value: string, onChange: (v: string) => void, props: Record<string, any> = {}) => (
    <Input size="sm" bg="white" value={value} onChange={(e) => onChange(e.target.value)} {...props} />
  );
  const usd = (value: number) => formatMoney(value, "USD");
  const brl = (value: number) => formatMoney(value, "BRL");
  const t = result.totals;

  const summary: [string, number, boolean?][] = [
    ["Preço de venda (custo final c/ créditos + margem + impostos)", t.salePrice * fx, true],
    ["(−) Custo da importação c/ créditos", t.finalCost * fx],
    ["(−) ICMS destacado", t.icmsHighlighted * fx],
    ["(−) IPI", t.saleIpi * fx],
    ["(−) PIS", t.pisSale * fx],
    ["(−) COFINS", t.cofinsSale * fx],
    ["(=) Margem bruta", t.grossMargin * fx, true],
    ["(−) IRPJ", t.irpj * fx],
    ["(−) CSLL", t.csll * fx],
    ["(=) Margem de contribuição 2", t.contributionMargin * fx, true],
    ["Substituição tributária (ST)", t.stBrl],
    ["Frete rodoviário", t.roadFreightBrl],
    ["(=) Valor da NF com ST", t.invoiceWithStBrl, true],
  ];
  // Fora do valor da NF: DIFAL (GNRE) e IBS/CBS destacados (2026 = ano-teste)
  const outside: [string, number][] = [
    ["DIFAL + FCP destino (recolhido à parte)", t.difalBrl],
    ["IBS destacado", t.ibsBrl],
    ["CBS destacada", t.cbsBrl],
  ].filter(([, value]) => (value as number) > 0) as [string, number][];

  return (
    <>
      <Card title="Operação" subtitle="Importação na moeda da invoice (USD); NF de saída em R$ pela taxa fiscal.">
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
          <Field label="Taxa fiscal USD (R$)">{input(exchange, setExchange)}</Field>
          <Field label="Frete internacional (USD)" hint="Rateado pelo peso">{input(freight, setFreight, { placeholder: "0,00" })}</Field>
          <Field label="Seguro (R$)" hint="Rateado pelo valor">{input(insurance, setInsurance, { placeholder: "0,00" })}</Field>
          <Field label="Taxas prepaid (USD)" hint="Rateadas pelo valor">{input(prepaid, setPrepaid, { placeholder: "0,00" })}</Field>
        </SimpleGrid>
        <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mt="16px" mb="6px">
          Pagamentos cambiais (diferencial cambial)
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
          {payments.map((p, index) => (
            <Flex key={index} gap="8px">
              <Field label={index === 0 ? "Antecipado (USD)" : "Saldo (USD)"}>
                {input(p.amount, (v) => setPayments((list) => list.map((x, i) => (i === index ? { ...x, amount: v } : x))), { placeholder: "0,00" })}
              </Field>
              <Field label="Taxa (R$)">
                {input(p.rate, (v) => setPayments((list) => list.map((x, i) => (i === index ? { ...x, rate: v } : x))), { placeholder: "0,0000" })}
              </Field>
            </Flex>
          ))}
          <Flex align="flex-end" pb="6px">
            <Text fontSize="12px" color={crm.textMuted}>
              Diferencial: {brl(t.exchangeDifferenceBrl)} (vazio = sem diferencial)
            </Text>
          </Flex>
        </SimpleGrid>
      </Card>

      <Card
        title="Itens"
        subtitle={
          setting
            ? `ICMS, ST/MVA e redução vêm da parametrização "${setting.name}" pelo NCM${branch ? ` e UF ${branch.state}` : ""}; pode editar.`
            : "Sem parametrização: informe ICMS, MVA e alíquota interna à mão (vazio = sem ST)."
        }
      >
        <TableCard>
          <Thead>
            <Tr>
              <Th {...thProps}>NCM</Th>
              <Th {...thProps}>Descrição</Th>
              <Th {...thProps}>Qtd</Th>
              <Th {...thProps}>Valor unit. (USD)</Th>
              <Th {...thProps}>Peso unit. (kg)</Th>
              <Th {...thProps}>II %</Th>
              <Th {...thProps}>IPI %</Th>
              <Th {...thProps}>PIS %</Th>
              <Th {...thProps}>COFINS %</Th>
              <Th {...thProps}>Antidumping (USD)</Th>
              <Th {...thProps}>ICMS %</Th>
              <Th {...thProps}>MVA %</Th>
              <Th {...thProps}>Interna destino %</Th>
              <Th {...thProps}>Red. BC ST %</Th>
              <Th {...thProps}>Frete rodov. (R$)</Th>
              <Th {...thProps} isNumeric>Preço unit. c/ IPI</Th>
              <Th {...thProps} isNumeric>ST unit.</Th>
              <Th {...thProps} isNumeric>Total unit. c/ ST</Th>
              <Th {...thProps} />
            </Tr>
          </Thead>
          <Tbody>
            {items.map((item, index) => {
              const calc = result.items[index];
              const cell = (field: keyof ItemDraft, w: string, placeholder?: string) => (
                <Td {...tdProps}>
                  <Input size="sm" w={w} value={item[field] as string} placeholder={placeholder} onChange={(e) => updateItem(item.key, { [field]: e.target.value })} />
                </Td>
              );
              return (
                <Tr key={item.key}>
                  <Td {...tdProps}>
                    <Input size="sm" w="110px" value={item.ncm} placeholder="0000.00.00" onChange={(e) => updateItem(item.key, { ncm: e.target.value })} onBlur={() => lookupNcm(item)} />
                    {item.rates === null && <Box mt="4px"><Pill {...toneColors.warning}>NCM sem tabela</Pill></Box>}
                    {item.fiscalNote && <Box mt="4px" title="Exceção da parametrização aplicada"><Pill {...toneColors.success}>{item.fiscalNote}</Pill></Box>}
                  </Td>
                  <Td {...tdProps}>
                    <Input size="sm" minW="150px" value={item.description} onChange={(e) => updateItem(item.key, { description: e.target.value })} />
                  </Td>
                  {cell("quantity", "64px")}
                  {cell("unitValue", "100px", "0,00")}
                  {cell("unitWeight", "80px", "0")}
                  {cell("ii", "60px")}
                  {cell("ipi", "60px")}
                  {cell("pis", "60px")}
                  {cell("cofins", "60px")}
                  {cell("dumping", "90px", "0,00")}
                  {cell("icms", "60px", toDraft(params.icmsRate))}
                  {cell("mva", "70px", "—")}
                  {cell("internal", "70px", "—")}
                  {cell("reduction", "70px", "—")}
                  {cell("roadFreight", "90px", "0,00")}
                  <Td {...tdProps} isNumeric whiteSpace="nowrap">{ready ? brl(calc.unitTotalBrl - calc.unitStBrl) : "—"}</Td>
                  <Td {...tdProps} isNumeric whiteSpace="nowrap">{ready ? brl(calc.unitStBrl) : "—"}</Td>
                  <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight="600">{ready ? brl(calc.unitTotalBrl) : "—"}</Td>
                  <Td {...tdProps}>
                    <IconButton aria-label="Remover item" size="sm" variant="ghost" colorScheme="red" icon={<FaTrash />} isDisabled={items.length === 1} onClick={() => setItems((list) => list.filter((i) => i.key !== item.key))} />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableCard>
        <Button mt="12px" size="sm" leftIcon={<FaPlus />} variant="outline" onClick={() => setItems((list) => [...list, newItem()])}>
          Adicionar item
        </Button>
      </Card>

      <Card title="Despesas aduaneiras (R$)" subtitle="Rateadas pelo peso, como na planilha.">
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
          <Field label="AFRMM">{input(afrmm, setAfrmm, { placeholder: "0,00" })}</Field>
          <Field label="Armazenagem e movimentação">{input(storage, setStorage, { placeholder: "0,00" })}</Field>
          <Field label="Honorários despachante">{input(broker, setBroker, { placeholder: "0,00" })}</Field>
          <Field label="Taxas destino">{input(destinationFees, setDestinationFees, { placeholder: "0,00" })}</Field>
          <Field label="Licenças (LI)">{input(license, setLicense, { placeholder: "0,00" })}</Field>
          <Field label="Taxa IBAMA" hint="Auto: toneladas × 0,7 × 80">{input(ibama, setIbama, { placeholder: fmt(ibamaAuto) })}</Field>
          <Field label="Daytona">{input(daytona, setDaytona, { placeholder: "0,00" })}</Field>
          <Field label="Crossdocking">{input(crossdocking, setCrossdocking, { placeholder: "0,00" })}</Field>
          <Field label="Frete rodoviário">{input(roadFreightExpense, setRoadFreightExpense, { placeholder: "0,00" })}</Field>
          <Field label="Ajudantes">{input(helpers, setHelpers, { placeholder: "0,00" })}</Field>
          <Field label="Taxa Siscomex">{input(siscomex, setSiscomex, { placeholder: "0,00" })}</Field>
          <Field label="Total">
            <Text fontSize="14px" fontWeight="700" pt="6px">{brl(expensesBrl)}</Text>
          </Field>
        </SimpleGrid>
      </Card>

      <Card
        title="Parâmetros"
        subtitle="Padrões da planilha Encomenda. Alterações valem só para esta simulação."
        action={
          <Button size="sm" variant="ghost" rightIcon={showParams ? <FaChevronUp /> : <FaChevronDown />} onClick={() => setShowParams((s) => !s)}>
            {showParams ? "Ocultar" : "Mostrar"}
          </Button>
        }
      >
        <Collapse in={showParams} animateOpacity>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
            {paramLabels.map(([key, label]) => (
              <Field key={key} label={label}>
                {input(paramDrafts[key], (v) => setParamDrafts((p) => ({ ...p, [key]: v })))}
              </Field>
            ))}
          </SimpleGrid>
          <Button mt="12px" size="xs" variant="link" color={crm.brand} onClick={() => setParamDrafts(initialParams)}>
            Restaurar padrões
          </Button>
        </Collapse>
      </Card>

      {ready ? (
        <Card title="Resultado" subtitle={`Taxa fiscal USD ${fmt(fx, 4)}${branch ? ` · filial ${branch.state}` : ""}`}>
          {missingSt && (
            <Box mb="12px">
              <Pill {...toneColors.warning}>Item com MVA e sem alíquota interna do destino: ST desse item não foi calculada</Pill>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px" mb="20px">
            <Stat label="Desembolso na importação" value={brl(t.disbursement * fx)} hint={usd(t.disbursement)} />
            <Stat label="Custo final c/ créditos" value={brl(t.finalCost * fx)} hint={usd(t.finalCost)} />
            <Stat label="NF sem ST (preço de venda)" value={brl(t.salePrice * fx)} hint={usd(t.salePrice)} />
            <Stat label="NF com ST" value={brl(t.invoiceWithStBrl)} highlight hint={`ST ${brl(t.stBrl)}`} />
          </SimpleGrid>
          <Box overflowX="auto">
            <Table size="sm">
              <Tbody>
                {summary.map(([label, value, strong]) => (
                  <Tr key={label}>
                    <Td {...tdProps} fontWeight={strong ? "700" : undefined}>{label}</Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight={strong ? "700" : undefined}>{brl(value)}</Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" color={crm.textMuted}>{fx > 0 ? usd(value / fx) : "—"}</Td>
                  </Tr>
                ))}
                {outside.map(([label, value]) => (
                  <Tr key={label}>
                    <Td {...tdProps} color={crm.textMuted}>{label} <Text as="span" fontSize="11px">(fora do valor da NF)</Text></Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" color={crm.textMuted}>{brl(value)}</Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" color={crm.textMuted}>{fx > 0 ? usd(value / fx) : "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      ) : (
        <Box w="95%" p="20px" textAlign="center" color={crm.textMuted} fontSize="14px">
          Informe a taxa fiscal e o valor de ao menos um item para ver a simulação.
        </Box>
      )}
    </>
  );
};

export default EncomendaSimulator;
